/*
 * Selectors: the questions the interface asks the model, written once so a component never
 * reaches into a slice by hand. Each is a pure function of the model and returns the stored
 * object where one exists (a selector that built a new object on every call would defeat the
 * identity the reducers preserve).
 *
 * Settings are read here too. The keys are the ones `internal/core/settings/keys.go` registers,
 * with the same defaults: a value the bootstrap carried wins when it has the registered type,
 * anything else falls back, so a stale or malformed stored value never reaches a control.
 */

import type { ConversationSlice, GroupView, Model, SelfUser } from "./model.ts"

export function selectMe(model: Model): SelfUser | null {
	return model.me
}

/** The groups an `@` may name — the composer's source and the group chip's data. */
export function selectMentionableGroups(model: Model): GroupView[] {
	return Object.values(model.groups).filter((g) => g.mentionable)
}

/**
 * What Home's catch-up shows, in the order it shows it (VISUAL.md §7.8): the reader's own
 * conversations with something unread — the ones that name them first, the newest otherwise.
 *
 * "Newest" is the server's order of `conversationOrder` (starred first, then the most recent
 * message — chat's bootstrap, `state/bootstrap.ts`): core's slice of a conversation carries no
 * timestamp of its own, and the order the server already sorted by is the one fact about recency
 * every reader of the model shares. A conversation the reader has left or merely reads by its
 * address is not in that list and therefore never a card.
 *
 * A pure function over the two slices it reads, so a component calls it inside `useMemo` over
 * `model.conversations` and `model.conversationOrder` rather than handing it to `useModel`: the
 * answer is a fresh array, and a selector that answers one re-renders for ever.
 */
export function unreadConversations(
	model: Pick<Model, "conversations" | "conversationOrder">,
): ConversationSlice[] {
	const unread = model.conversationOrder.flatMap((id) => {
		const slice = model.conversations[id]
		return slice !== undefined && slice.unread > 0 && slice.meta.archivedAt === undefined
			? [slice]
			: []
	})
	return [...unread.filter((s) => s.mentions > 0), ...unread.filter((s) => s.mentions === 0)]
}

/**
 * Home's count line ("14 unread in 6 conversations · 2 mentions"), over exactly the conversations
 * `unreadConversations` draws cards for. The sentence and the cards under it are one statement, so
 * they are counted once: a Home that counted across every slice the model holds announced a mention
 * no card on the screen led to (measured with one archived conversation).
 *
 * A fresh object on every call, like the array above, so a component memoises it over the two slices
 * rather than handing it to `useModel`.
 */
export function catchUpTotals(model: Pick<Model, "conversations" | "conversationOrder">): {
	conversations: number
	messages: number
	mentions: number
} {
	const waiting = unreadConversations(model)
	return {
		conversations: waiting.length,
		messages: waiting.reduce((sum, slice) => sum + slice.unread, 0),
		mentions: waiting.reduce((sum, slice) => sum + slice.mentions, 0),
	}
}

// ---- settings ----

/**
 * How close together the message list draws its rows (X-A4 N17). The pair is the closed set
 * `keys.go` validates, and it is declared once here because three places read it: the appearance
 * screen's radio rows, the attribute `core/shell/preferences.tsx` stamps on the document, and the
 * stylesheet's `[data-density]` overrides.
 */
export const DENSITIES = ["comfortable", "compact"] as const

export type Density = (typeof DENSITIES)[number]

/** The user settings core registers, with the defaults of keys.go. */
export interface CoreSettings {
	"core.shell.theme": "system" | "light" | "dark"
	"core.shell.density": Density
	"core.shell.sidebar_collapsed": string
	"core.shell.seen_welcome": boolean
	"core.shell.announce_messages": boolean
	"core.shell.announce_verbosity": "senderBody" | "senderTimeBody"
	"core.shell.timezone": string | null
	"core.shell.shortcut_overrides": Record<string, string[]>
	"core.composer.enter_sends": boolean
	"core.composer.preview_open": boolean
	"core.composer.source_mode": boolean
	"core.composer.undo_send_seconds": number
	"core.copy.transcript_fields": { author: boolean; time: boolean; permalink: boolean }
}

/**
 * The four holds A19 offers for undo-send, in seconds; anything else keeps the default, and the
 * default is the first of them: no hold at all (A-08 §5). The order is the settings screen's.
 */
export const UNDO_SEND_CHOICES: readonly number[] = [0, 5, 10, 30]

export type SettingKey = keyof CoreSettings

type Accept<K extends SettingKey> = (value: unknown) => value is CoreSettings[K]

interface SettingDef<K extends SettingKey> {
	fallback: CoreSettings[K]
	accept: Accept<K>
}

const isBoolean = (value: unknown): value is boolean => typeof value === "boolean"

function oneOf<T extends string>(...values: T[]): (value: unknown) => value is T {
	return (value): value is T => typeof value === "string" && (values as string[]).includes(value)
}

function isOneOfNumbers(values: readonly number[]): (value: unknown) => value is number {
	return (value): value is number => typeof value === "number" && values.includes(value)
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value)
}

const SETTINGS: { [K in SettingKey]: SettingDef<K> } = {
	"core.shell.theme": { fallback: "system", accept: oneOf("system", "light", "dark") },
	// The first of DENSITIES is the shipped one, as `keys.go` ships it; the set is spread rather
	// than repeated so a third density would need no edit here.
	"core.shell.density": { fallback: DENSITIES[0], accept: oneOf(...DENSITIES) },
	// The folded sidebar sections, as their ids one space apart — keys.go validates the shape. The
	// ids are the modules' own, so the catalogue knows only that the value is a string; empty is
	// "every section open", which is what an account that has never folded one has.
	"core.shell.sidebar_collapsed": {
		fallback: "",
		accept: (value): value is string => typeof value === "string",
	},
	// False is "has not been greeted yet": a new account sees Home's welcome until it writes this.
	"core.shell.seen_welcome": { fallback: false, accept: isBoolean },
	"core.shell.announce_messages": { fallback: true, accept: isBoolean },
	"core.shell.announce_verbosity": {
		fallback: "senderBody",
		accept: oneOf("senderBody", "senderTimeBody"),
	},
	"core.shell.timezone": {
		fallback: null,
		accept: (value): value is string | null => value === null || typeof value === "string",
	},
	"core.shell.shortcut_overrides": {
		fallback: {},
		accept: (value): value is Record<string, string[]> =>
			isRecord(value) &&
			Object.values(value).every(
				(keys) => Array.isArray(keys) && keys.every((k) => typeof k === "string"),
			),
	},
	"core.composer.enter_sends": { fallback: true, accept: isBoolean },
	"core.composer.preview_open": { fallback: false, accept: isBoolean },
	// False: the formatted editor is everybody's composer and the markdown source view is the
	// escape hatch a person turns on for themselves (F-2 §17, D5). `keys.go` registers the same
	// value; this one answers a model that has no settings map yet.
	"core.composer.source_mode": { fallback: false, accept: isBoolean },
	// No hold: a send goes the moment Enter is pressed unless the account asked for a window.
	"core.composer.undo_send_seconds": { fallback: 0, accept: isOneOfNumbers(UNDO_SEND_CHOICES) },
	"core.copy.transcript_fields": {
		fallback: { author: true, time: true, permalink: true },
		accept: (value): value is CoreSettings["core.copy.transcript_fields"] =>
			isRecord(value) &&
			isBoolean(value["author"]) &&
			isBoolean(value["time"]) &&
			isBoolean(value["permalink"]),
	},
}

/** One setting out of a settings map: the stored value when it has the registered type, else the default. */
export function settingOf<K extends SettingKey>(
	values: Record<string, unknown>,
	key: K,
): CoreSettings[K] {
	const def = SETTINGS[key]
	const value = values[key]
	return def.accept(value) ? value : def.fallback
}

/** The same read against the whole model, for a component that has one. */
export function selectSetting<K extends SettingKey>(model: Model, key: K): CoreSettings[K] {
	return settingOf(model.settings, key)
}
